import React from 'react';
import styled from 'styled-components';

const PageWrapper = styled.div`
  width: 100%;
`;

const HeaderImage = styled.div`
  width: 100%;
  height: 320px;
  background: linear-gradient(rgba(12, 35, 64, 0.7), rgba(12, 35, 64, 0.8)), 
              url('/images/footer-pages/privacy-header.jpg') no-repeat center center;
  background-size: cover;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const HeaderTitle = styled.h1`
  font-size: 48px;
  color: white;
  text-align: center;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 0;
  padding: 0;
`;

const PrivacyContainer = styled.div`
  max-width: 1100px;
  margin: 50px auto 60px;
  padding: 0 20px;
`;

const PageTitle = styled.h1`
  font-size: 36px;
  color: #0c2340;
  margin-bottom: 30px;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: #05a0e8;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  font-style: italic;
  color: #777;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #0c2340;
  margin-bottom: 20px;
`;

const Paragraph = styled.p`
  margin-bottom: 20px;
  line-height: 1.7;
  color: #444;
`;

const UnorderedList = styled.ul`
  margin-bottom: 20px;
  padding-left: 20px;
  
  li {
    margin-bottom: 10px;
    line-height: 1.7;
    color: #444;
  }
`;

const ContentImage = styled.div`
  width: 100%;
  height: 250px;
  background-image: url('/images/footer-pages/privacy-content.jpg');
  background-size: cover;
  background-position: center;
  margin: 30px 0;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const ImageCaption = styled.p`
  text-align: center;
  font-style: italic;
  color: #666;
  margin-top: -5px;
  margin-bottom: 30px;
  font-size: 14px;
`;

const PrivacyPolicy = () => {
  return (
    <PageWrapper>
      <HeaderImage>
        <HeaderTitle>Privacy Policy</HeaderTitle>
      </HeaderImage>
      
      <PrivacyContainer>
        <LastUpdated>Last Updated: June 15, 2023</LastUpdated>
        
        <Section>
          <Paragraph>
            Ocean Crown ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by Ocean Crown. This Privacy Policy applies to our website, and its associated subdomains (collectively, our "Service"). By accessing or using our Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service.
          </Paragraph>
        </Section>
        
        <ContentImage />
        <ImageCaption>Data protection and privacy are important to us at Ocean Crown.</ImageCaption>
        
        <Section>
          <SectionTitle>Definitions and Key Terms</SectionTitle>
          <Paragraph>
            For this Privacy Policy:
          </Paragraph>
          <UnorderedList>
            <li><strong>Cookie:</strong> A small amount of data generated by a website and saved by your web browser. It is used to identify your browser, provide analytics, and remember information about you such as your language preference or login information.</li>
            <li><strong>Company:</strong> When this policy mentions "Company," "we," "us," or "our," it refers to Ocean Crown that is responsible for your information under this Privacy Policy.</li>
            <li><strong>Country:</strong> Where Ocean Crown or the owners/founders of Ocean Crown are based, in this case is Nigeria.</li>
            <li><strong>Customer:</strong> Refers to the company, organization, or person that signs up to use the Ocean Crown Service.</li>
            <li><strong>Device:</strong> Any internet-connected device such as a phone, tablet, computer, or any other device that can be used to visit Ocean Crown and use the services.</li>
            <li><strong>Service:</strong> Refers to the website, and any other related services provided by Ocean Crown.</li>
            <li><strong>Third-party service:</strong> Refers to advertisers, contest sponsors, promotional and marketing partners, and others who provide our content or whose products or services we think may interest you.</li>
            <li><strong>Website:</strong> Ocean Crown's site, which can be accessed via this URL: www.oceancrown.com</li>
            <li><strong>You:</strong> A person or entity that is registered with Ocean Crown to use the Services.</li>
          </UnorderedList>
        </Section>
        
        <Section>
          <SectionTitle>Information We Collect</SectionTitle>
          <Paragraph>
            We collect information from you when you visit our website, register on our site, place an order, subscribe to our newsletter, respond to a survey, fill out a form, or use our services.
          </Paragraph>
          <UnorderedList>
            <li><strong>Personal Information:</strong> Name, email address, phone numbers, shipping address, billing address, and payment information.</li>
            <li><strong>Business Information:</strong> Company name, business type, industry information.</li>
            <li><strong>Shipping Details:</strong> Cargo information, origin, destination, and shipping preferences.</li>
            <li><strong>Usage Data:</strong> Information about how you use our website, products, and services.</li>
          </UnorderedList>
        </Section>
        
        <Section>
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Paragraph>
            We use the information we collect in various ways, including to:
          </Paragraph>
          <UnorderedList>
            <li>Provide, operate, and maintain our website and services</li>
            <li>Improve, personalize, and expand our website and services</li>
            <li>Understand and analyze how you use our website and services</li>
            <li>Develop new products, services, features, and functionality</li>
            <li>Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the website, and for marketing and promotional purposes</li>
            <li>Process your transactions</li>
            <li>Find and prevent fraud</li>
            <li>Comply with legal and regulatory requirements</li>
          </UnorderedList>
        </Section>
        
        <Section>
          <SectionTitle>Log Files</SectionTitle>
          <Paragraph>
            Ocean Crown follows a standard procedure of using log files. These files log visitors when they visit websites. All hosting companies do this as part of hosting services' analytics. The information collected by log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked to any information that is personally identifiable. The purpose of the information is for analyzing trends, administering the site, tracking users' movement on the website, and gathering demographic information.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Cookies and Web Beacons</SectionTitle>
          <Paragraph>
            Like any other website, Ocean Crown uses 'cookies'. These cookies are used to store information including visitors' preferences, and the pages on the website that the visitor accessed or visited. The information is used to optimize the users' experience by customizing our web page content based on visitors' browser type and/or other information.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Third-Party Privacy Policies</SectionTitle>
          <Paragraph>
            Ocean Crown's Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you to consult the respective Privacy Policies of these third-party ad servers for more detailed information. It may include their practices and instructions about how to opt-out of certain options.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Children's Information</SectionTitle>
          <Paragraph>
            Ocean Crown does not knowingly collect any Personal Identifiable Information from children under the age of 13. If you think that your child provided this kind of information on our website, we strongly encourage you to contact us immediately and we will do our best efforts to promptly remove such information from our records.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Online Privacy Policy Only</SectionTitle>
          <Paragraph>
            This Privacy Policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or collect in Ocean Crown. This policy is not applicable to any information collected offline or via channels other than this website.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Consent</SectionTitle>
          <Paragraph>
            By using our website, you hereby consent to our Privacy Policy and agree to its Terms and Conditions.
          </Paragraph>
        </Section>
        
        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about this Privacy Policy, please contact us:
          </Paragraph>
          <Paragraph>
            <strong>Email:</strong> info@oceancrown.com<br />
            <strong>Phone:</strong> +234 123 456 7890<br />
            <strong>Address:</strong> 123 Ocean Street, Lagos, Nigeria
          </Paragraph>
        </Section>
      </PrivacyContainer>
    </PageWrapper>
  );
};

export default PrivacyPolicy; 